/** Epic 93 — Understand Risk panel copy and formatters (presentation only). */

import type { PatternVolumeKind } from './patternReviewScopeSemantics';
import type { ReviewWindowContext } from './reviewWindowContext';
import { formatObservationBadge } from './reviewWindowContext';
import { ownerRoleIsUnassigned, PATTERN_VOLUME_SIGNALS_DISCLAIMER } from './incidentPatternVocabulary';
import { formatFollowUpStatusDisplayLabel } from './followUpStatusSemantics';

export const RECURRING_RISK_DECISION_LABEL = 'Manage this risk pattern';

export function formatUnderstandRiskActionOwnerLine(
  followUpOwner: string | null | undefined,
  actionOwnerLabel: string,
): string {
  const label = String(actionOwnerLabel || 'Action owner').trim();
  if (ownerRoleIsUnassigned(followUpOwner)) {
    return `${label}: Unassigned — assigned in the corrective action step.`;
  }
  return `${label}: ${String(followUpOwner || '').trim()}`;
}

export const UNDERSTAND_RISK_HEATMAP_TITLE = 'When does this repeat?';

export const UNDERSTAND_RISK_HEATMAP_HELPER =
  'Shows which hours the pattern was observed. This is a pattern-observation view, not a count of confirmed incidents.';

export function formatUnderstandRiskFrequencyPrimary(
  count: number,
  reviewWindowContext: ReviewWindowContext,
  _kind: PatternVolumeKind = 'review_window',
): string {
  void _kind;
  return formatObservationBadge(count, reviewWindowContext);
}

export function buildRecurringRiskSummaryLine(args: {
  patternTitle?: string | null;
  zoneLabel?: string | null;
  fallbackSummary?: string | null;
}): string {
  const title = String(args.patternTitle || '').trim();
  const zone = String(args.zoneLabel || '').trim();
  const fallback = String(args.fallbackSummary || '').trim();

  if (title) {
    const normalized = title.replace(/\s+/g, ' ');
    if (zone && !normalized.toLowerCase().includes(zone.toLowerCase())) {
      return `${normalized} was observed repeatedly in the ${zone}.`;
    }
    return normalized.endsWith('.') ? normalized : `${normalized}.`;
  }
  if (fallback) {
    return fallback.endsWith('.') ? fallback : `${fallback}.`;
  }
  return 'Review this recurring risk pattern before verifying representative evidence.';
}

export function formatUnderstandRiskFollowUpStatus(
  followUpState: string | null | undefined,
  workflowStatus?: string | null,
): string {
  const raw = String(followUpState || '').trim();
  if (!raw || /^needs review$/i.test(raw)) {
    return formatFollowUpStatusDisplayLabel('new', workflowStatus);
  }
  if (/^not started$/i.test(raw)) return 'Not started';
  if (/^no next review/i.test(raw)) return 'Not started';
  return raw;
}

export function formatUnderstandRiskNextReview(summary: string | null | undefined): string {
  const raw = String(summary || '').trim();
  if (!raw || /^no next review/i.test(raw) || /^not set$/i.test(raw)) return 'Not set';
  return raw;
}

export function formatUnderstandRiskWorkOrder(summary: string | null | undefined): string {
  const raw = String(summary || '').trim();
  if (!raw || /no work order|not linked/i.test(raw)) return 'Not linked';
  return raw;
}

export { PATTERN_VOLUME_SIGNALS_DISCLAIMER as UNDERSTAND_RISK_FREQUENCY_DISCLAIMER };
